import { CustomerType, ProductType } from "@core/contracts/data-contracts";

export interface LoginPayload {
  email: string;
  password: string;
}

export interface TokenData {
  token: string;
  refreshToken: string;
}

export interface RefreshTokenPayload {
  accessToken: string;
  refreshToken: string;
}

export interface AdPreferences {
  offersOn: boolean;
  newsletterOn: boolean;
  personalTipsOn: boolean;
}

export interface CloudStorageBannerWidget {
  id: string;
  primaryTitle: string;
  primaryBackgroundImage: {
    url: string;
  };
  secondaryTitle: string;
  storageIcon: {
    url: string;
  };
  secondaryBackgroundColor: string;
  price: string;
  priceWithDiscount: string;
  info: string;
  startDate: Date;
  endDate: Date;
  isImportant: boolean;
  secondarySubTitleInactive: string;
  secondarySubTitleActive: string;
  primaryButtonTitleActive: string;
  primaryButtonUrlActive: string;
  secondaryButtonTitleInactive: string;
  secondaryButtonUrlInactive: string;
  primaryButtonTitleInactive: string;
}

export interface FaqWidget {
  title: string;
  subTitle: string;
  faqList: FaqItemWidget[];
}

export interface LinkWidget {
  title: string;
  linkList: LinkItemWidget[];
}

export interface MiniBannerListWidget {
  bannerList: MiniBannerWidget[];
  startDate: Date;
  endDate: Date;
}

export interface MiniBannerWidget {
  id: string;
  title: string;
  subTitle: string;
  icon: {
    url: string;
  };
  backgroundImage: {
    url: string;
  };
  linkIcon: string;
  googlePlayUrl: string;
  appStoreUrl: string;
  isMobileAppBanner: boolean;
  url: string;
  startDate: Date;
  endDate: Date;
  visibleForProduct: ProductType;
  visibleForCustomer: CustomerType;
}

export interface MobileAppBannerWidget {
  id: string;
  primaryTitle: string;
  primaryBackgroundImage: {
    url: string;
  };
  secondaryTitle: string;
  secondarySubTitle: string;
  secondaryBackgroundColor: string;
  appStoreUrl: string;
  googlePlayUrl: string;
  standOutImage: {
    url: string;
  };
  startDate: Date;
  endDate: Date;
  visibleForProduct: ProductType;
  visibleForCustomer: CustomerType;
}

export interface LinkItemWidget {
  title: string;
  url: string;
  isNewTab: boolean;
  visibleForProduct: ProductType;
  visibleForCustomer: CustomerType;
}

export interface FaqItemWidget {
  question: string;
  answer: string;
  answer2: string;
  visibleForProduct: ProductType;
  visibleForCustomer: CustomerType;
}

export interface PersonalOfferWidget {
  primaryBackgroundImage: {
    url: string;
  };
  standOutImage: {
    url: string;
    alternativeText: string;
  };
}

export enum WidgetType {
  CloudStorageBannerWidget = 'CloudStorageBannerWidget',
  FaqWidget = 'FaqWidget',
  LinkWidget = 'LinkWidget',
  MiniBannerListWidget = 'MiniBannerListWidget',
  MiniBannerWidget = 'MiniBannerWidget',
  MobileAppBannerWidget = 'MobileAppBannerWidget',
  PersonalOfferWidget = 'PersonalOfferWidget',
}

export interface WidgetResponse {
  id: number;
  __component: string;
}

export interface Widget {
  type: WidgetType;
  data: any;
}

export interface Page {
  translationMap: string;
  widgetList: Widget[];
}

export enum CmsPageUrl {
  Overview = 'overview-page',
  Subscription = 'subscription-page',
  Usage = 'usage-page',
  TopUp = 'top-up-page',
  Invoices = 'invoices-page',
  DataOutsideEu = 'data-outside-eu-page',
  PremiumWallet = 'premium-wallet-page',
  ChangeSUbscription = 'change-subscription',
  Prolongation = 'prolongation-page',
  AddSubscription = 'add-subscription-page',
  PaymentSettings = 'payment-settings-page',
  CloudStorage = 'cloud-storage-page',
  Settings = 'settings-page',
  CommunicationAndPrivacy = 'communication-and-privacy-page',
  SimcardSettigns = 'sim-card-settings-page',
  NetworkSettings = 'network-settings-page',
}

export enum ButtonType {
  Black = 'Black',
  Red = 'Red',
  Border = 'Border',
  White = 'White',
  Underline = 'Underline',
  Text = 'Text',
  Icon = 'Icon',
}

export interface Option {
  label: string;
  id: string;
}

export interface UsageDownload {
  from: Date;
  to: Date;
  isOnlyExtra: boolean;
}

export interface WalletUsageDownload {
  from: Date;
  to: Date;
}

export enum MessageStatus {
  Success = 'Success',
  Info = 'Info',
  Error = 'Error',
}

export interface RadioButton {
  label: string;
  isSelected: boolean;
}

export interface RadioOption {
  label?: string;
  value: number | string;
  isSelected?: boolean;
  isActive?: boolean;
  isRequested?: boolean;
  isSemibold?: boolean;
  price?: number;
  priceWithDiscount?: number;
  customPrice?: string;
  description?: string;
  wordToReplacePrice?: string;
  requested?: boolean;
  message?: string;
}

export interface Modal {
  data: any;
}

export interface UsageChart {
  megabytesRemaining: number;
  megabytesTotal: number;
  minutesRemaining: number;
  minutesTotal: number;
  untilNewCredit: number;
  extraPrice: number;
  isDataOnly: boolean | null;
}

export interface Invoice {
  date: string;
  status: string;
  id: number;
  price: number;
}

export interface Subscription {
  name: string;
  startDate: Date;
  endDate: Date;
  prolongationDate: Date;
  contractDuration: number;
}

export interface SubscriptionShort {
  name: string;
  description: string;
  price: number;
  priceWithDiscount?: number;
}

export interface Costs {
  subscription: SubscriptionShort;
  extras: SubscriptionShort[];
}

export interface InternetUsage {
  date: Date;
  country: string;
  bytesAmount: number;
  extraPrice: number;
}

export interface InternetUsageViewModel {
  total: number;
  extraPrice: number;
  list: InternetUsage[];
}

export interface CallSmsUsage {
  dateTime: Date;
  phoneNumber: string;
  secondsAmount: number;
  extraPrice: number;
}

export interface CallSmsUsageViewModel {
  total: number;
  extraPrice: number;
  list: CallSmsUsage[];
}

export interface ExtraInternetBundle {
  title: string;
  megabytesAmount: number;
  price: number;
  id: number;
}

export interface ExtraCallSmsBundle {
  title: string;
  minutesAmount: number;
  subTypeId: number;
  price: number;
  id: number;
}

export enum CloudStorageStatus {
  Active = 'Active',
  Inactive = 'NotActive',
}

export enum BundleType {
  CallSms = 1,
  Internet = 2,
}

export enum BundleSubType {
  CallSms = 3,
  Internet = 2,
  VoiceCeiling = 31,
  DataCeiling = 32,
  ExtraVSMS = 28,
  InternationalVoice = 6,
  InternationalData = 21,
}

export interface Bundle {
  isActive: boolean;
  id?: number;
  displayName?: string;
  subscriptionDurationInMonths?: number;
  subscriptionTypeId?: number;
  subscriptionSubTypeId?: number;
  subscriptionPrice?: number;
  bundleSize?: number;
  discountSize?: number;
  isAvailable?: boolean;
  isUnlimited?: boolean;
}

export interface DataOutsideEUItem {
  country: string;
  id: number;
  displayName: string;
  displayNameShort: string;
  displayNameMobile: string;
  subscriptionTypeId: number;
  subscriptionSubTypeId: number;
  subscriptionPrice: number;
  discountName: any;
  discountSize: number;
  discountDurationInMonths: number;
  subscriptionDurationInMonths: number;
  bundleSize: number;
  isAvailable: boolean;
}

export interface DataOutsideEUItemViewModel {
  id: number;
  displayNameShort: string;
  usedAmount: number;
  amount: number;
  typeId: number;
  startDate: Date;
  endDate: Date;
}

export interface DataOutsideEUResponse {
  available: DataOutsideEUItem[];
  bought: DataOutsideEUItemViewModel[];
}

export enum SimType {
  Physical = 0,
  eSim = 1,
}

export interface IsAllowedResponse {
  status: VerificationStatus;
}

export enum InvoiceStatus {
  Pending = 'pending',
  Processing = 'processing',
  Authorized = 'authorized',
  Settled = 'settled',
  Refunded = 'refunded',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

export enum VerificationStatus {
  Ok = 'Ok',
  Error = 'Error',
}

export enum DateSort {
  DateAsc = 'DateAsc',
  DateDesc = 'DateDesc',
}

export enum ActionStatus {
  Initial = 'Initial',
  Pending = 'Pending',
  Cancelled = 'Cancelled',
  Success = 'Success',
  Failed = 'Failed',
}

export enum BundleUnit {
  MB = 'MB',
  Min = 'Min',
}

export interface NavigationSubLink {
  label: string;
  path: string;
  showWhenLimited?: boolean;
  showWhenTerminated?: boolean;
  hideForDataOnly?: boolean;
  hideForB2B?: boolean;
  hideForInternet?: boolean;
}

export interface NavigationLink {
  label: string;
  path: string;
  showWhenLimited?: boolean;
  showWhenTerminated?: boolean;
  showWhenJottaInUse?: boolean;
  hideForDataOnly?: boolean;
  hideForB2B?: boolean;
  hideForInternet?: boolean;
  icon?: string;
  hasActiveSubLinks?: boolean;
  subLinks: NavigationSubLink[];
}

export enum KeyboardKey {
  ArrowDown = 'ArrowDown',
  ArrowUp = 'ArrowUp',
  Enter = 'Enter',
}

export interface CeilingBundle {
  id?: number;
  displayName?: string;
  description?: string;
  subscriptionPrice?: number;
  subscriptionSubTypeId?: number;
  subscriptionTypeId?: number;
}

export enum MessageType {
  On = 'on',
  Off = 'off',
}

export interface EsimProfile {
  convertedProfile: string;
  activationCode: string;
  smdpAddress: string;
}

export interface SubLink {
  label: string;
  path: string;
}

export interface LinkGroup {
  icon?: string;
  label: string;
  path: string;
  showWhenLimited?: boolean;
  showWhenTerminated?: boolean;
  subLinks?: SubLink[];
}

export enum InstallationStepStatus {
  Done = 'done',
  Active = 'active',
  Pending = 'pending',
}

export enum InstallationStepId {
  Hardware = 'hardware',
  Activation = 'activation',
  Installation = 'installation',
  SelfInstallation = 'selfInstallation',
}

export interface InstallationDeliveryItem {
  label: string;
  date: string;
}

export interface InstallationStepDetails {
  items: InstallationDeliveryItem[];
}

export interface InstallationStep {
  id: InstallationStepId;
  status: InstallationStepStatus;
  completedAt?: string;
  activeSince?: string;
  activationDate?: string;
  installationDate?: string;
  details?: InstallationStepDetails;
}
